import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ThumbsUp } from "lucide-react";
import { useMemo } from "react";

import { AdminDataTable } from "~/components/admin/data-table";
import { Badge } from "~/components/ui/badge";
import { EmptyState } from "~/components/ui/empty-state";
import { requirePermission } from "~/lib/functions/admin-access";
import { adminFeatureVoteCountsQueryOptions } from "~/lib/functions/admin-feature-votes";
import type { FeatureVoteCountRow } from "~/lib/server/feature-votes";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import type { FeatureVoteStatus } from "~/lib/shared/feature-votes/items";
import { nextSortSearch } from "~/lib/shared/sort";

const SORTS = ["item", "votes", "paying", "trial", "last30Days"] as const;
type FeatureVoteSort = (typeof SORTS)[number];

const SORT_DEFAULTS = listSortDefaults<FeatureVoteSort>("votes", [
  "votes",
  "paying",
  "trial",
  "last30Days",
]);

const SORT_COLUMNS: Record<FeatureVoteSort, SortColumn<FeatureVoteCountRow>> = {
  item: { kind: "text", value: (row) => row.title },
  votes: { kind: "number", value: (row) => row.total },
  paying: { kind: "number", value: (row) => row.paying },
  trial: { kind: "number", value: (row) => row.trial },
  last30Days: { kind: "number", value: (row) => row.last30Days },
};

export const Route = createFileRoute("/admin/feature-votes/")({
  staticData: { title: "Feature Votes" },
  validateSearch: listSearchSchema(SORTS),
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "feature-votes:read"),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(
      adminFeatureVoteCountsQueryOptions,
    );
  },
  component: FeatureVotesAdminPage,
});

const STATE_BADGES: Record<
  FeatureVoteStatus["state"],
  { label: string; variant: "outline-primary" | "success" | "cancelled" }
> = {
  planned: { label: "Coming soon", variant: "outline-primary" },
  shipped: { label: "Shipped", variant: "success" },
  dropped: { label: "Dropped", variant: "cancelled" },
};

const count = (
  id: Exclude<FeatureVoteSort, "item">,
  header: string,
  value: (row: FeatureVoteCountRow) => number,
): ColumnDef<FeatureVoteCountRow> => ({
  id,
  header,
  meta: { align: "end" },
  cell: ({ row }) => (
    <span className="tabular-nums">{value(row.original)}</span>
  ),
});

const COLUMNS: ColumnDef<FeatureVoteCountRow>[] = [
  {
    id: "item",
    header: "Item",
    cell: ({ row }) => (
      <span className="font-medium text-gray-900">{row.original.title}</span>
    ),
  },
  {
    id: "where",
    header: "Where coaches vote",
    cell: ({ row }) => (
      <span className="text-gray-500">
        {row.original.whereVoted.join(", ") || "—"}
      </span>
    ),
  },
  count("votes", "Votes", (row) => row.total),
  count("paying", "Paying", (row) => row.paying),
  count("trial", "Trial", (row) => row.trial),
  count("last30Days", "Last 30 days", (row) => row.last30Days),
  {
    id: "state",
    header: "State",
    cell: ({ row }) => {
      const badge = STATE_BADGES[row.original.state];
      return (
        <Badge size="sm" variant={badge.variant}>
          {badge.label}
        </Badge>
      );
    },
  },
];

function FeatureVotesAdminPage(): React.ReactElement {
  const { data } = useQuery(adminFeatureVoteCountsQueryOptions);
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const sort = search.sort ?? SORT_DEFAULTS.sort;
  const direction = search.dir ?? SORT_DEFAULTS.direction(sort);

  const rows = useMemo(
    () =>
      sortListRows(
        data ?? [],
        sort,
        direction,
        SORT_COLUMNS,
        (row) => row.title,
      ),
    [data, sort, direction],
  );

  const changeSort = (column: FeatureVoteSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, SORT_DEFAULTS),
      }),
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-medium text-gray-900">Feature votes</h1>
        <p className="mt-1 text-sm text-gray-500">
          How many people want each planned feature, from the Coming soon cards
          and What's coming. Counts only, no names.
        </p>
      </div>

      <AdminDataTable
        data={rows}
        columns={COLUMNS}
        getRowId={(row) => row.key}
        sorts={SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        emptyState={
          <EmptyState
            size="sm"
            icon={ThumbsUp}
            title="No items to vote for"
            description="Feature vote items are listed in code."
          />
        }
      />

      <p className="text-xs text-gray-500">
        "Paying" and "Trial" use each voter's NOVA plan today. The other votes
        come from coaches before a trial, after cancelling, or on free access.
      </p>
    </div>
  );
}
